import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import UploadAnalytics from './upload-analytics';

import { builderDispatch, WebUI } from '../_utils/ui';
import screenAndStyle from '../_utils/screen-and-style';
import { PusherConnection } from '../_mobile-uploader/pusher-connection';
import QrCode from './mobile-import/qr-code';
import QrCodeUploading from './mobile-import/qr-uploading';
import UploadFailureMessage from './mobile-import/upload-failure-message';
import Spinner from './mobile-import/spinner';
import { fetchDesktopUploaderConfig } from './mobile-import/desktop-uploader-config';
import { StatusBox } from '../_mobile-uploader/status-box';
import { defaultNewAlbumTitle } from './default-new-album-title';
import AnalyticsHandler from '../_libs/analytics/checkout-google-analytics';

// Re-fetch the desktop config at this fraction of its lifetime so the QR and
// the Pusher auth token never lapse while the loader sits open.
const CONFIG_REFRESH_LEAD = 0.9;
// How long to wait before retrying after a failed refresh.
const CONFIG_REFRESH_RETRY_SECONDS = 60;

class MyMobileImport extends React.Component {
  constructor() {
    super();
    const { App } = window;

    this.state = {
      loggedIn: App && App.order.logged_in(),
      loaderServiceWasDisplayed: false,
      imageUploading: false,
      loading: true,
      fileCount: 0,
      failedUploadCount: 0,
    };
    this.files = this.files || [];
  }

  filesSelected() {
    return [];
  }

  clear() {}

  clearUploaded() {}

  restart() {}

  pause() {}

  totalNumberOfFilesSelected() {
    return 0;
  }

  performUpload() {}

  componentDidMount() {
    this.builderDispatchToken = builderDispatch.register(async (payload) => {
      switch (payload.type) {
        case WebUI.LOGIN:
          const updatedChannelConfig = await fetchDesktopUploaderConfig();
          if (this.isUnmounted) break;

          this.setState({
            loggedIn: true,
            ...updatedChannelConfig,
          });
          this.refreshBeforeExpiry(updatedChannelConfig?.expiresInSeconds);
          break;

        case WebUI.PHOTO_SERVICE_SELECTED:
          if (payload.service === 'mobile') {
            this.initialiseQrCodeImage();
          }
          break;

        case WebUI.UPLOAD_PROGRESS:
          this.setState((prevState) => ({
            prevState: (prevState.fileCount -= 1),
          }));
          break;

        case WebUI.UPLOAD_DONE:
          this.setState({ imageUploading: false });
          !this.state.failedUploadCount && $('.image-loader').modal('hide');
          break;
      }
    });

    this.initialiseQrCodeImage();
  }

  componentWillUnmount() {
    // A timer that already fired leaves an in-flight fetch that clearTimeout
    // cannot cancel; this flag stops its continuation touching a dead component.
    this.isUnmounted = true;
    clearTimeout(this.configRefreshTimer);
    builderDispatch.unregister(this.builderDispatchToken);
  }

  async initialiseQrCodeImage() {
    if (!this.state.loaderServiceWasDisplayed) {
      const channelConfig = await fetchDesktopUploaderConfig();
      if (this.isUnmounted) return;

      this.setState({
        loaderServiceWasDisplayed: true,
        loading: false,
        ...channelConfig,
      }); // Only initiate Pusher connection when tab/service is selected
      this.refreshBeforeExpiry(channelConfig?.expiresInSeconds);
    }
  }

  scheduleConfigRefreshIn(seconds) {
    clearTimeout(this.configRefreshTimer);
    if (!seconds) return;

    this.configRefreshTimer = setTimeout(
      this.refreshChannelConfig,
      seconds * 1000,
    );
  }

  // Lead factor keeps the refresh strictly before expiry for any lifetime.
  refreshBeforeExpiry(expiresInSeconds) {
    this.scheduleConfigRefreshIn(
      expiresInSeconds ? expiresInSeconds * CONFIG_REFRESH_LEAD : 0,
    );
  }

  refreshChannelConfig = async () => {
    const channelConfig = await fetchDesktopUploaderConfig();
    if (this.isUnmounted) return;

    // A transient failure must not tear down a session whose token is still
    // valid; keep the current config and retry shortly.
    if (!channelConfig || channelConfig.error) {
      this.scheduleConfigRefreshIn(CONFIG_REFRESH_RETRY_SECONDS);
      return;
    }

    this.setState({ ...channelConfig });
    this.refreshBeforeExpiry(channelConfig.expiresInSeconds);
  };

  sendAnalytics(eventCategory) {
    const eventLabel = UploadAnalytics.eventTypes(
      this.files.map((f) => f.uploadAnalytics),
    );

    const payload = {
      type: WebUI.Analytics.SEND,
      eventCategory: eventCategory,
      eventAction: 'mobile-device',
      eventLabel: eventLabel,
      eventValue: 0,
    };

    AnalyticsHandler.handleAnalyticsEvent(payload);
  }

  handlePusherEvent = (event) => {
    const { data: images = [] } = event;
    const numberOfFiles = images.length;

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_BEGUN,
      number_of_files: numberOfFiles,
      album: defaultNewAlbumTitle(), // Allow Prism Builder to use default album title after image upload
    });

    images.forEach((image) => {
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_PROGRESS,
        data: image,
        number_of_files: numberOfFiles,
      });
    });

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_DONE,
      number_of_errors: 0,
      number_of_files: numberOfFiles,
    });
  };

  handleIncomingUploads = ({ fileCount }) => {
    this.setState({
      imageUploading: true,
      fileCount: fileCount,
      failedUploadCount: 0,
    });
  };

  handleUploadFail = ({ failedUploadCount }) => {
    this.setState({
      imageUploading: false,
      failedUploadCount: failedUploadCount,
    });
  };

  handleUploadingQrCodeButton = () => {
    this.setState({ imageUploading: false });
  };

  render() {
    const {
      loaderServiceWasDisplayed,
      imageUploading,
      fileCount,
      failedUploadCount,
      mobileUploaderUrl,
      config,
      loading,
      error,
    } = this.state;

    if (loading) {
      return (
        <MobileImportWrapper {...this.props}>
          <Spinner />
        </MobileImportWrapper>
      );
    }

    if (error) {
      return (
        <MobileImportWrapper {...this.props}>
          <StatusBox type="danger" className="w-50 mx-auto mt-2">
            <i className="fas fa-exclamation-circle fa-lg mr-2 text-danger" />
            {error}
          </StatusBox>
        </MobileImportWrapper>
      );
    }

    return (
      <MobileImportWrapper {...this.props}>
        {loaderServiceWasDisplayed && (
          <PusherConnection
            config={config}
            eventHandlers={{
              'client-image-upload': this.handlePusherEvent,
              'client-image-uploading': this.handleIncomingUploads,
              'client-image-upload-fail': this.handleUploadFail,
            }}>
            {imageUploading ? (
              <QrCodeUploading
                fileCount={fileCount}
                handleUploadingQrCodeButton={this.handleUploadingQrCodeButton}
              />
            ) : (
              <div className="image-loader__qr-code-wrapper">
                <div className="image-loader__qr-code-instructions">
                  <i className="d-none d-xl-block mx-auto fas fa-mobile-alt align-self-center" />
                  {this.props.height > 350 && (
                    <h4 className="d-none d-lg-block title service-title font-weight-bold">
                      Uploading photos from your phone is as simple as 1-2-3
                    </h4>
                  )}
                  <small>
                    <ol>
                      <li>Scan the QR code by using your phone camera</li>
                      <li>Select the photos you want to use</li>
                      <li>Upload your photos</li>
                    </ol>
                  </small>
                </div>
                <div>
                  <QrCode url={mobileUploaderUrl} />
                </div>
              </div>
            )}

            {!!failedUploadCount && (
              <UploadFailureMessage failedUploadCount={failedUploadCount} />
            )}
          </PusherConnection>
        )}
      </MobileImportWrapper>
    );
  }
}

export const MobileImportWrapper = ({ service, height, children }) => {
  return (
    <div
      style={{ height: height / 1.3 }}
      data-test-id={`image-loader-service-mobile`}
      id={`image-loader-service-mobile`}
      className={classNames('text-center image-loader-service mobile')}
      tabIndex="0">
      <div>{children}</div>
    </div>
  );
};

MyMobileImport.propTypes = {
  service: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
};

export default MyMobileImport;
